// EXTERNAL DEPENDENCIES
import { useCallback, useEffect, useState } from 'react'

// HELPERS
import { resizeImage, usePrefixedContent } from '@/utils'

// API
import { useAssetValidation } from '@/api/requests'

// CONSTANTS
import { DEFAULT_BACKGROUND_PHOTO } from '@/common/images/profile'
import {
  COVER_PHOTO_DIMENSION_REQUIREMENTS,
  PROFILE_PHOTO_DIMENSION_REQUIREMENTS,
} from '@/constants'

// TYPES
import { PhotoTypes } from '@/types'

/**
 * Provides the helper functions needed to update and validate
 * the uploaded photos
 */
export default function usePhotoUploading({
  isLoading,
  photoSrc,
  photoType,
  onDelete,
  updateImageSrc,
  updatePhotoValidity,
}: Readonly<{
  isLoading: boolean
  photoSrc: string
  photoType: PhotoTypes
  onDelete?: () => void
  updateImageSrc: (
    photoType: PhotoTypes,
    imageSrc: string,
    isFinishedValidating?: boolean,
  ) => void
  updatePhotoValidity: (photoType: PhotoTypes, isValid: boolean) => void
}>) {
  // NETWORK REQUESTS
  const { mutateAsync: validatePhoto } = useAssetValidation()

  const getContent = usePrefixedContent(
    'components.ui.profile.upload-photo-section.',
  )
  const generalPhotoUploadErrorText = getContent('generalPhotoUploadError')
  const failedPhotoUploadErrorText = getContent('failedPhotoUploadError')
  const ariaLabelText = {
    profilePhoto: getContent('profilePhotoUploadScreenReaderText'),
    coverPhoto: getContent('coverPhotoUploadScreenReaderText'),
  }[photoType]

  // STATE
  const [updatedPhotoSrc, setUpdatedPhotoSrc] = useState(photoSrc)
  const [imageResizeDimensions, setImageResizeDimensions] = useState(null)
  const [isCoverPhoto, setIsCoverPhoto] = useState(false)
  const [isEditingPhoto, setIsEditingPhoto] = useState(false)
  const [isCroppingPhoto, setIsCroppingPhoto] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // CLICK HANDLERS
  const closePhotoCropScreen = useCallback(() => {
    setUpdatedPhotoSrc(photoSrc)
    setIsEditingPhoto(false)
    setIsCroppingPhoto(false)
  }, [photoSrc])

  const handleError = useCallback(
    (newErrorMessage: string) => {
      closePhotoCropScreen()
      setErrorMessage(newErrorMessage)
    },
    [closePhotoCropScreen],
  )

  const handleEditPhotoClick = () => {
    setIsEditingPhoto(true)
    updatePhotoValidity(photoType, false)
  }

  const finalHandleEditPhotoClick =
    !isLoading && photoSrc ? handleEditPhotoClick : null //NOSONAR

  const closeCropScreenAndHandlePhotoRemoval = (...args: unknown[]) => {
    closePhotoCropScreen()

    const lastArg = args.pop()
    const shouldDeletePhoto = typeof lastArg === 'boolean' ? lastArg : false
    const noExistingPhotoToOverwrite = !photoSrc

    if (noExistingPhotoToOverwrite || shouldDeletePhoto) {
      //NOSONAR
      setUpdatedPhotoSrc(null)
      updatePhotoValidity(photoType, true)
    }

    if (shouldDeletePhoto) {
      updatePhotoValidity(photoType, true)
      setErrorMessage('')
      updateImageSrc(photoType, '', true)
      onDelete?.()
    }
  }

  const updatePhotoLocallyAndOpenCropScreen = async (
    uncroppedPhoto: string,
  ) => {
    try {
      const resizedImage = await resizeImage(
        uncroppedPhoto,
        imageResizeDimensions,
      )

      setIsCroppingPhoto(true)
      setUpdatedPhotoSrc(resizedImage)
      updatePhotoValidity(photoType, false)
    } catch (_e) {
      handleError(generalPhotoUploadErrorText)
    }
  }

  const updatePhotoAndCloseCropScreen = async (croppedImage: string) => {
    closePhotoCropScreen()

    updateImageSrc(photoType, croppedImage, false)

    try {
      const { assetUrl } = await validatePhoto({
        request: {
          type: 'image',
          content: croppedImage,
          variation: photoType,
        },
      })

      if (assetUrl) {
        updatePhotoValidity(photoType, true)
        updateImageSrc(photoType, assetUrl, true)
      } else {
        throw new Error()
      }
    } catch (_e) {
      handleError(failedPhotoUploadErrorText)
      updatePhotoValidity(photoType, true)
      updateImageSrc(photoType, '', true)
    }
  }

  // EFFECTS
  useEffect(() => {
    setUpdatedPhotoSrc(photoSrc)
  }, [photoSrc])

  useEffect(() => {
    const isCoverPhoto = photoType === 'coverPhoto'
    setIsCoverPhoto(isCoverPhoto)
    setImageResizeDimensions(
      isCoverPhoto
        ? COVER_PHOTO_DIMENSION_REQUIREMENTS
        : PROFILE_PHOTO_DIMENSION_REQUIREMENTS,
    )
  }, [photoType])

  useEffect(() => {
    return () => {
      setUpdatedPhotoSrc('')
      setImageResizeDimensions(null)
      setIsCoverPhoto(false)
      setIsEditingPhoto(false)
      setIsCroppingPhoto(false)
      setErrorMessage('')
    }
  }, [])

  const needsToUploadCoverPhoto =
    !photoSrc || photoSrc.includes(DEFAULT_BACKGROUND_PHOTO)

  return {
    photoSrc,
    ariaLabelText,
    updatedPhotoSrc,
    isCoverPhoto,
    isEditingPhoto,
    isCroppingPhoto,
    errorMessage,
    needsToUploadCoverPhoto,
    setErrorMessage,
    handleEditPhotoClick: finalHandleEditPhotoClick,
    closeCropScreenAndHandlePhotoRemoval,
    updatePhotoLocallyAndOpenCropScreen,
    updatePhotoAndCloseCropScreen,
    updatePhotoValidity,
  }
}
